<template>
  <div
    id="event-form"
    class="mt-3"
    v-if="event && eventForm">
    <!-- Form Field Add/Edit Form -->
    <b-modal
      id="form-field-modal"
      ref="formFieldModal"
      v-model="formFieldModal"
      :title="`${isAttachedField ? 'Edit' : 'Add'} Form Element`"
      @hidden="resetFormFieldForm"
      hide-footer>
      <b-form
        id="form-field-form"
        name="formFieldForm">
        <!-- Existing Or Custom -->
        <div class="row">
          <div class="col-sm">
            <label for="event-form-existing">Element:</label>
            <v-select
              id="event-form-existing"
              ref="eventFormExisting"
              name="eventFormExisting"
              class="text-capitalize"
              placeholder="Select Element"
              label="label"
              index="id"
              @input="setField(existingFieldID)"
              :options="existingFieldOptions"
              v-model="existingFieldID">
            </v-select>
          </div>
        </div>
        <!-- / Existing Or Custom -->

        <!-- Field Type -->
        <div
          v-if="existingFieldID === 'custom'"
          class="row mt-3">
          <div class="col-sm">
            <label for="event-form-type">Type: {{eventFormField.type}}</label>
            <b-form-select
              id="event-form-type"
              ref="eventFormType"
              name="eventFormType"
              class="text-capitalize"
              v-model="eventFormField.type">
              <option selected :value="undefined">Select Type</option>
              <optgroup
                v-for="(fieldTypes, key) in fieldTypeOptions"
                :key="key"
                :label="key">
                <option
                  v-for="t in fieldTypes"
                  :key="t.value"
                  :value="t.value">
                  {{t.text}}
                </option>
              </optgroup>
            </b-form-select>
          </div>
        </div>
        <!-- / Field Type -->

        <!-- Field Label -->
        <div
          class="row mt-3"
          v-if="eventFormField.type !== 'static-paragraph' && existingFieldID === 'custom'">
          <div class="col">
            <label for="event-form-label">
              <span v-if="eventFormField.type === 'static-header'">Heading Text:</span>
              <span v-else>Label:</span>
            </label>
            <b-form-input
              id="event-form-label"
              ref="eventFormLabel"
              name="eventFormLabel"
              type="text"
              v-model="eventFormField.label"
              :placeholder="customFieldLabelPlaceholder">
            </b-form-input>
          </div>
        </div>
        <!-- / Field Label -->

        <!-- Paragraph Text -->
        <div
          class="row mt-3"
          v-if="eventFormField.type === 'static-paragraph' && existingFieldID === 'custom'">
          <div class="col">
            <label for="event-form-paragraph">Text:</label>
            <b-form-textarea
              id="event-form-paragraph"
              ref="eventFormParagraph"
              name="eventFormParagraph"
              rows="6"
              v-model="eventFormField.label"
              :placeholder="customFieldLabelPlaceholder">
            </b-form-textarea>
          </div>
        </div>
        <!-- / Paragraph Text -->

        <!-- Field Dropdown Options -->
        <div
          class="row mt-3"
          v-if="eventFormField.type === 'dropdown' && existingFieldID === 'custom'">
          <div class="col">
            <label for="event-form-options">Options:</label>
            <b-form-input
              id="event-form-options"
              ref="eventFormOptions"
              name="eventFormOptions"
              type="text"
              v-model="eventFormField.options"
              placeholder="e.g. Small,Medium,Large">
            </b-form-input>
          </div>
        </div>
        <!-- / Field Dropdown Options -->

        <!-- Link URL --->
        <div
          class="row mt-3"
          v-if="eventFormField.type === 'static-link' && existingFieldID === 'custom'">
          <div class="col">
            <label for="event-form-url">URL:</label>
            <b-form-input
              id="event-form-url"
              ref="eventFormURL"
              name="eventFormURL"
              type="text"
              v-model="eventFormField.meta.url"
              placeholder="Enter this link's url">
            </b-form-input>
          </div>
        </div>
        <!-- / Link URL --->

        <!-- Required, Waiver, Hidden -->
        <div class="row mt-3">
          <!-- Field Required -->
          <div
            v-if="!staticField"
            class="col-auto">
            <b-form-checkbox
              id="event-form-required"
              name="eventFormRequired"
              ref="eventFormRequired"
              v-model="eventFormField.required">
              Required
            </b-form-checkbox>
          </div>
          <!-- / Field Required -->

          <!-- Waiver Flag -->
          <div
            v-if="!layoutField"
            class="col-auto">
            <b-form-checkbox
              id="event-form-waiver"
              name="eventFormWaiver"
              ref="eventFormWaiver"
              v-model="eventFormField.waiver">
              Waiver
            </b-form-checkbox>
          </div>
          <!-- / Waiver Flag -->

          <!-- Hidden Flag -->
          <div
            v-if="!layoutField"
            class="col-auto">
            <b-form-checkbox
              id="event-form-hidden"
              name="eventFormHidden"
              ref="eventFormHidden"
              v-model="eventFormField.hidden">
              Hidden
            </b-form-checkbox>
          </div>
          <!-- / Hidden Flag -->
        </div>
        <!-- / Required, Waiver, Hidden -->

        <!-- Form Buttons -->
        <div class="mt-4 d-flex justify-content-end">
          <b-button
            type="submit"
            variant="success"
            @click.prevent="handleFormField"
            :disabled="!existingFieldID || submitting">
            {{isAttachedField ? 'Save' : 'Add'}} Field
            <font-awesome-icon icon="check-circle"></font-awesome-icon>
          </b-button>

          <b-button
            type="button"
            class="ml-3"
            variant="danger"
            @click="$refs.formFieldModal.hide();"
            :disabled="submitting">
            Cancel
            <font-awesome-icon icon="times-circle"></font-awesome-icon>
          </b-button>
        </div>
        <!-- / Form Buttons -->
      </b-form>
    </b-modal>
    <!-- / Form Field Add/Edit Form -->

    <!-- Price Modifiers Modal -->
    <b-modal
      id="price-modifiers-modal"
      ref="priceModifiersModal"
      size="lg"
      v-model="priceModifiersModalOpen"
      title="Price Modifiers"
      @hidden="resetPriceModifiersForm"
      hide-footer>
      <b-form
        id="price-modifiers-form"
        name="priceModifiersForm">
        <!-- Price Modifiers Table -->
        <div class="row">
          <div class="col-sm">
            <b-table
              striped
              hover
              stacked="lg"
              small
              show-empty
              @row-clicked="showAddEditPriceModifier"
              :items="priceModifiers"
              :fields="priceModifierFields"
              empty-text="No Price Modifiers to show.">
            </b-table>
          </div>
        </div>
        <!-- / Price Modifiers Table -->

        <!-- Form Buttons -->
        <div class="mt-4 d-flex justify-content-end">
          <b-button
            type="button"
            variant="primary"
            v-if="priceModifierField"
            @click="showAddEditPriceModifier({
              form_field: priceModifierField,
              form_field_id: priceModifierField.id,
            })">
            Add Price Modifier
            <font-awesome-icon icon="plus-circle"></font-awesome-icon>
          </b-button>

          <b-button
            type="button"
            class="ml-3"
            variant="danger"
            @click="$refs.priceModifiersModal.hide();">
            Close
            <font-awesome-icon icon="times-circle"></font-awesome-icon>
          </b-button>
        </div>
        <!-- / Form Buttons -->
      </b-form>
    </b-modal>
    <!-- / Price Modifiers Modal -->

    <!-- Add/Edit Price Modifier Modal -->
    <b-modal
      id="add-edit-price-modifier-modal"
      ref="addEditPriceModifierModal"
      v-model="addEditPriceModifierModalOpen"
      :title="`${priceModifier.id ? 'Edit' : 'Add'} Price Modifier`"
      @hidden="resetAddEditPriceModifierForm"
      @shown="focusInput('priceModifierDescription')"
      hide-footer>
      <b-form
        id="add-edit-price-modifier-form"
        name="addEditPriceModifierForm"
        @submit.prevent="submitPriceModifier(priceModifier)">
        <!-- Applies To Field -->
        <div class="row">
          <!-- Applies To Field -->
          <div class="col">
            <label>Applies To Field:</label>
            <h6>{{priceModifier.form_field.label}}</h6>
          </div>
          <!-- Applies To Field -->
        </div>
        <!-- / Applies To Field -->

        <!-- Description -->
        <div class="row mt-3">
          <!-- Description -->
          <div class="col">
            <label for="price-modifier-description">Description:</label>
            <b-form-input
              id="price-modifier-description"
              ref="priceModifierDescription"
              name="priceModifierDescription"
              type="text"
              placeholder="Describe this price modifier"
              v-model="priceModifier.description"
              :readonly="submitting">
            </b-form-input>
          </div>
          <!-- / Description -->
        </div>
        <!-- / Description -->

        <!-- Response -->
        <div class="row mt-3">
          <!-- Response -->
          <div class="col">
            <label for="price-modifier-response-equals">
              If Response Equals:
              <small class="text-warning ml-auto d-inline-block">Ignores case.</small>
            </label>

            <!-- Drop Down -->
            <b-form-select
              v-if="priceModifier.form_field.type === 'dropdown'"
              id="price-modifier-response-equals"
              ref="priceModifierResponseEquals"
              name="priceModifierResponseEquals"
              v-model="priceModifier.form_field_value"
              :readonly="submitting">
              <option selected :value="undefined">Select Option</option>
              <option
                v-for="o in priceModifier.form_field.options"
                :key="o"
                :value="o">
                {{o}}
              </option>
            </b-form-select>
            <!-- / Drop Down -->

            <!-- Checkbox -->
            <b-form-radio-group
              name="priceModifierResponseEquals"
              v-else-if="priceModifier.form_field.type === 'checkbox'"
              v-model="priceModifier.form_field_value">
              <b-form-radio value="true">True</b-form-radio>
              <b-form-radio value="false">False</b-form-radio>
            </b-form-radio-group>
            <!-- / Checkbox -->

            <!-- Otherwise -->
            <b-form-input
              v-else
              id="price-modifier-response-equals"
              ref="priceModifierResponseEquals"
              name="priceModifierResponseEquals"
              type="text"
              placeholder="Enter the value that triggers a price change"
              v-model="priceModifier.form_field_value"
              :readonly="submitting">
            </b-form-input>
            <!-- / Otherwise -->
          </div>
          <!-- / Response -->
        </div>
        <!-- / Response -->

        <!-- Price Adjustment -->
        <div class="row mt-3">
          <!-- Price Adjustment -->
          <div class="col mt-3 mt-lg-0">
            <label for="price-modifier-price-adjustment">Price Adjustment:</label>
            <Number
              id="price-modifier-price-adjustment"
              ref="priceModifierPriceAdjustment"
              name="priceModifierPriceAdjustment"
              type="currency"
              step="10"
              min="-10000"
              placeholder="Enter the adjustment to price (+/-)"
              v-model="priceModifier.price_modification_flat"
              :readonly="submitting"
            />
          </div>
          <!-- / Price Adjustment -->
        </div>
        <!-- / Price Adjustment -->

        <!-- Form Buttons -->
        <div class="mt-4 d-flex justify-content-end">
          <b-button
            type="submit"
            variant="success"
            :disabled="submitting">
            Save
            <font-awesome-icon icon="check-circle"></font-awesome-icon>
          </b-button>

          <b-button
            v-if="priceModifier.id"
            type="button"
            class="ml-3"
            variant="danger"
            @click="submitPriceModifier(priceModifier.id)"
            :disabled="submitting">
            Delete
            <font-awesome-icon icon="times-circle"></font-awesome-icon>
          </b-button>

          <b-button
            type="button"
            class="ml-3"
            variant="danger"
            @click="$refs.addEditPriceModifierModal.hide();"
            :disabled="submitting">
            Cancel
            <font-awesome-icon icon="times-circle"></font-awesome-icon>
          </b-button>
        </div>
        <!-- / Form Buttons -->
      </b-form>
    </b-modal>
    <!-- / Add/Edit Price Modifier Modal -->

    <!-- Form Preview -->
    <div id="form-preview">
      <!-- Page Header -->
      <div class="container-fluid">
        <div class="row">
          <div class="col">
            <div class="d-flex flex-column flex-sm-row align-items-center">
              <h3 class="mb-0 mr-3 text-center text-sm-left">
                Registration Form: {{event.name}}
              </h3>

              <!-- Recall Saved Forms -->
              <b-dropdown
                v-if="forms.length > 0 && countFormFields === 0"
                class="link-dropdown"
                variant="link"
                text="Copy Form">
                <b-dropdown-item
                  v-for="form in forms"
                  :key="form.id"
                  @click="applySavedForm(form)">
                  {{form.name}}
                </b-dropdown-item>
              </b-dropdown>
              <!-- / Recall Saved Forms -->

              <!-- Price Modifiers -->
              <b-button
                type="button"
                variant="link"
                v-if="formPriceModifiers.length > 0"
                @click="showPriceModifiers()">
                {{formPriceModifiers.length}}
                Price Modifier{{formPriceModifiers.length > 1 ? 's' : ''}}
              </b-button>
              <!-- / Price Modifiers -->

              <!-- Hidden Fields -->
              <div
                v-if="formHiddenFields.length > 0">
                {{formHiddenFields.length}}
                Hidden Field{{formHiddenFields.length > 1 ? 's' : ''}}
              </div>
              <!-- / Hidden Fields -->

              <div class="flex-fill"></div>

              <b-form-checkbox
                class="mt-1 mt-sm-0"
                v-model="hideButtons">
                Preview Mode
              </b-form-checkbox>
            </div>
            <hr>
          </div>
        </div>
      </div>
      <!-- / Page Header -->

      <div class="container mx-auto">
        <!-- Always Collected -->
        <RegistrantBaseData
          v-if="!hideButtons"
          class="mt-0"
          display-only
        />
        <!-- / Always Collected -->

        <!-- Form Preview -->
        <FormFields
          class="mt-3"
          @edit-field="editFormField"
          @remove-field="unattachField"
          @reorder-fields="reorderFields"
          @add-price-modifier="showAddEditPriceModifier"
          @show-price-modifiers="showPriceModifiers"
          :form-fields="orderedFields"
          :hide-buttons="hideButtons"
          :price-modifiers="formPriceModifiers"
        />
        <!-- / Form Preview -->

        <!-- Empty Form Message -->
        <div
          id="empty-form"
          class="d-flex flex-column justify-content-center text-center p-3"
          v-if="forms.length > 0 && countFormFields === 0">
          <small class="d-block font-italic">
            This form has no additional fields. Add one by clicking the
            Add Field <font-awesome-icon size="sm" icon="plus"></font-awesome-icon> button.
            <br>
            You may also copy fields from one of your previous forms.
          </small>
        </div>
        <!-- / Empty Form Message -->
      </div>
    </div>
    <!-- / Form Preview -->

    <div class="corner-buttons">
      <!-- Add Predefined Field Button -->
      <b-dropdown
        id="layout-add"
        class="mr-3 shadow"
        dropup
        variant="primary">
        <template slot="button-content">
          <div class="px-3">
            <font-awesome-icon icon="level-up-alt"></font-awesome-icon> Add Newline
          </div>
        </template>
        <b-dropdown-header>Layout</b-dropdown-header>
        <b-dropdown-divider />
        <b-dropdown-item
          v-for="field in predefinedLayout"
          :key="field.id"
          @click="addLayoutFormField(field)">
          {{field.label}}
        </b-dropdown-item>
      </b-dropdown>
      <!-- / Add Predefined Field Button -->

      <!-- Add Custom Field Button -->
      <b-button
        class="shadow"
        type="button"
        variant="success"
        @click="formFieldModal = true;">
        <font-awesome-icon icon="plus"></font-awesome-icon> Add Field
      </b-button>
      <!-- / Add Custom Field Button -->
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import vSelect from 'vue-select';
import DataSvc from '../services/data';
import AppMixin from '../services/app-mixin';
import RegistrantBaseData from '../components/RegistrantBaseData.vue';
import Formatters from '../services/formatters';
import EventBus from '../services/event-bus';

const defaultPriceModifier = { form_field: {} };

export default {
  name: 'event-form',
  components: {
    RegistrantBaseData,
    vSelect,
  },
  mixins: [
    AppMixin,
  ],
  data() {
    return {
      event: false,
      eventForm: false,
      eventFormField: {},
      eventFormFieldCopy: {},
      existingFieldID: undefined,
      formFieldModal: false,
      fieldTypeOptions: [],
      predefinedLayout: [],
      hideButtons: false,
      forms: [],
      existing: [],

      // price modifiers
      submitting: false,
      priceModifier: _.clone(defaultPriceModifier),
      addEditPriceModifierModalOpen: false,
      priceModifiersModalOpen: false,
      priceModifierField: null,
      priceModifierFields: [
        {
          key: 'description',
        },
        {
          key: 'form_field.label',
          label: 'Applies To Field',
        },
        {
          key: 'form_field_value',
          label: 'If Response Equals',
        },
        {
          key: 'price_modification_flat',
          label: 'Price Adjustment',
          class: 'text-md-right',
          formatter: (value) => {
            const prepend = value >= 0 ? '+' : '';
            return `${prepend}${Formatters.currency(value)}`;
          },
        },
      ],
      formPriceModifiers: [],
    };
  },
  computed: {
    existingFieldOptions() {
      const translateType = (type) => {
        let translated = type.replace(/-/g, ' ');
        const typeMap = {
          text: 'small text input',
          textarea: 'large text input',
          'static paragraph': 'small text',
          'static header': 'large text',
        };

        if (typeMap[translated]) {
          translated = typeMap[translated];
        }

        return translated;
      };

      const mapped = _.map(this.existing, (f) => {
        f.label = `${f.label} (${translateType(f.type)})`;
        return f;
      });

      return [{
        id: 'custom',
        label: 'Create New Element',
      }].concat(mapped);
    },
    orderedFields() {
      return _.orderBy(this.eventForm.form_fields, 'order');
    },
    staticField() {
      const staticFields = [
        'static-link',
        'static-paragraph',
        'static-header',
      ];

      return staticFields.includes(this.eventFormField.type);
    },
    layoutField() {
      const layoutFields = [
        'layout-linebreak',
        'layout-hr',
      ];

      return layoutFields.includes(this.eventFormField.type);
    },
    isAttachedField() {
      return (this.eventFormFieldCopy.form_ids || []).includes(this.eventForm.id);
    },
    countFormFields() {
      return this.eventForm.form_fields.length;
    },
    nextFormFieldIndex() {
      return (_.last(this.orderedFields) || { order: -1 }).order + 1;
    },
    customFieldLabelPlaceholder() {
      switch (this.eventFormField.type) {
        case 'static-header':
          return 'Enter heading text e.g. "ATTENTION"';
        case 'static-paragraph':
          // eslint-disable-next-line
          return 'Enter single- or multi-line text e.g.\n\nBy clicking "continue" you agree to all terms and conditions previously laid out herein.\n\nFurthermore you agree to bring all issues to the event admin 3 hours prior to event start.';
        case 'static-link':
          return 'Enter link text e.g. "Click Here To Visit Website"';
        case 'text':
          return 'e.g. "Favorite Color:" or "What\'s your favorite color?"';
        case 'number':
          return 'e.g. "How many hours a day do you sleep?"';
        case 'textarea':
          return 'e.g. "List Special Food Instructions:"';
        case 'checkbox':
          return 'e.g. "I agree to do 10 push-ups opon arrival"';
        case 'dropdown':
          return 'e.g. "Select your size:"';
        default:
          return 'Enter text';
      }
    },

    // price modifiers
    priceModifiers() {
      let priceModifiers = this.formPriceModifiers;

      if (this.priceModifierField) {
        priceModifiers = _.filter(
          this.formPriceModifiers,
          { form_field_id: this.priceModifierField.id },
        );
      }

      return _.orderBy(priceModifiers, 'form_field.label');
    },

    // hidden fields
    formHiddenFields() {
      return _.filter(this.orderedFields, { hidden: true });
    },
  },
  methods: {
    editFormField(formField) {
      const f = _.cloneDeep(formField);

      if (_.isArray(f.options)) {
        f.options = _.join(f.options, ',');
      }

      this.existingFieldID = f.id;
      this.eventFormFieldCopy = _.cloneDeep(formField);

      this.$nextTick(() => {
        this.eventFormField = f;
        this.$refs.formFieldModal.show();
      });
    },
    handleFormField() {
      let formFieldPromise = Promise.resolve();
      const f = _.cloneDeep(this.eventFormField);
      const updateSelf = f.id && f.id === this.eventFormFieldCopy.id;

      // validate that new field isn't already attached
      // AND that field ID changed
      if (f.form_ids
          && f.form_ids.includes(this.eventForm.id)
          && !updateSelf) {
        formFieldPromise = this.showWarning(
          'You have already attached this element to your registration form.',
          'Element already attached',
        );
        return formFieldPromise;
      }

      this.showToast('Updating Form...');
      this.submitting = true;

      // if keeping same element but updating required or waiver
      if (updateSelf) {
        formFieldPromise = DataSvc.updateFormsFormField(this.eventForm.id, f);
      // if using new element
      } else {
        // if field already attached, preserve its order and remove it
        if (this.isAttachedField) {
          f.order = this.eventFormFieldCopy.order;
          formFieldPromise = DataSvc.removeFormField(this.eventForm.id, this.eventFormFieldCopy.id);
        // otherwise append new field to end of current form
        } else {
          f.order = this.nextFormFieldIndex;
        }

        // if new form field, create + attach
        if (typeof this.eventFormField.id === 'undefined') {
          formFieldPromise = formFieldPromise.then(() => DataSvc.createEventFormField(this.eventForm.id, f));
        // if existing field, attach only
        } else {
          formFieldPromise = formFieldPromise.then(() => DataSvc.attachExistingFormField(this.eventForm.id, f));
        }
      }

      return formFieldPromise
        .then(this.getFormAndOrgFields)
        .then(() => {
          this.formFieldModal = false;
          this.showToast('Form updated!');
          return this.delay(1000);
        })
        .catch((reason) => {
          if (reason.status !== 400) {
            this.showToast('Something went wrong saving the Event form fields.');
          }
        })
        .finally(() => {
          this.hideToast();
          this.submitting = false;
        });
    },
    setField(fieldID) {
      if (fieldID === null) return;

      let field = {
        organization_id: this.loggedInUser.actingOrg.id,
        required: false,
        waiver: false,
        meta: {},
      };

      if (fieldID !== 'custom') {
        const existing = _.find(this.existing, { id: fieldID });
        field = _.cloneDeep(existing);
      }

      field.form_id = this.eventForm.id;
      this.eventFormField = field;
    },
    addLayoutFormField(formField) {
      const f = _.cloneDeep(formField);
      f.order = this.nextFormFieldIndex;
      this.showToast('Updating Form...');
      return DataSvc.createEventFormField(this.eventForm.id, f)
        .then(this.getFormAndOrgFields)
        .then(() => {
          this.showToast('Form updated!');
          return this.delay(1000);
        })
        .finally(this.hideToast);
    },
    unattachField(field) {
      this.showToast('Updating Form...');
      return DataSvc.removeFormField(this.eventForm.id, field.id)
        .then(this.getFormAndOrgFields)
        .then(() => {
          this.showToast('Form updated!');
          return this.delay(1000);
        })
        .finally(this.hideToast);
    },
    reorderFields(reorderedFields) {
      const fieldOrder = {};

      reorderedFields.forEach((f, i) => {
        const field = _.find(
          this.eventForm.form_fields,
          { id: f.id },
        );

        field.order = i;
        fieldOrder[f.id] = i;
      });

      this.showToast('Updating Form...');

      return DataSvc.reorderFormFields(this.eventForm.id, fieldOrder)
        .then(() => {
          this.showToast('Form updated!');
          return this.delay(1000);
        })
        .finally(this.hideToast);
    },
    applySavedForm(form) {
      this.eventForm.form_fields = _.cloneDeep(form.form_fields);
      this.showToast('Updating Form...');
      return DataSvc.copyExistingForm(this.eventForm.id, form.id)
        .then(() => {
          this.showToast('Form updated!');
          return this.delay(1000);
        })
        .finally(this.hideToast);
    },
    getFormAndOrgFields(formID) {
      return Promise.all([
        this.getForm(formID),
        this.getOrgFormFields(),
      ]);
    },
    getForm(formID) {
      return DataSvc.getForm(this.eventForm.id || formID)
        .then((eventForm) => {
          this.eventForm = eventForm;
        });
    },
    getOrgFormFields() {
      return DataSvc.getFormFields({ organization_id: this.loggedInUser.actingOrg.id })
        .then((formFields) => {
          this.predefinedLayout = _(formFields)
            .filter(p => p.type.includes('layout'))
            .uniqBy('label')
            .orderBy('label')
            .value();

          // set form fields
          this.existing = _(formFields)
            .reject(p => p.type.includes('layout'))
            .orderBy('label')
            .value();
        });
    },
    resetFormFieldForm() {
      this.existingFieldID = undefined;
      this.eventFormField = {};
      this.eventFormFieldCopy = {};
    },

    // price modifiers
    showPriceModifiers(field) {
      this.priceModifiersModalOpen = true;
      this.priceModifierField = _.cloneDeep(field);
    },
    resetPriceModifiersForm() {
      this.priceModifierField = null;
    },
    showAddEditPriceModifier(priceModifier) {
      this.addEditPriceModifierModalOpen = true;

      if (!priceModifier.id && priceModifier.form_field.type === 'checkbox') {
        priceModifier.form_field_value = 'true';
      }

      this.priceModifier = _.cloneDeep(priceModifier);
    },
    resetAddEditPriceModifierForm() {
      this.priceModifier = _.clone(defaultPriceModifier);
    },
    submitPriceModifier(priceModifier) {
      let priceModifierPromise;

      this.submitting = true;

      if (typeof priceModifier !== 'object') {
        priceModifierPromise = this.deletePriceModifier(priceModifier);
      } else if (priceModifier.id) {
        priceModifierPromise = this.updatePriceModifier(priceModifier);
      } else {
        priceModifierPromise = this.createPriceModifier(priceModifier);
      }

      priceModifierPromise
        .then(() => {
          this.getPriceModifiers(this.eventForm.id);
        })
        .finally(() => this.delay(1000))
        .finally(() => {
          this.submitting = false;
          this.hideToast();
        });

      return priceModifierPromise;
    },
    createPriceModifier(priceModifier) {
      this.showToast('Creating price modifier...');

      return DataSvc.createPriceModifier(this.eventForm.id, priceModifier)
        .then(() => {
          this.addEditPriceModifierModalOpen = false;
          this.showToast('Price modifier created!');
        });
    },
    updatePriceModifier(priceModifier) {
      this.showToast('Updating price modifier...');

      return DataSvc.updatePriceModifier(this.eventForm.id, priceModifier)
        .then(() => {
          this.addEditPriceModifierModalOpen = false;
          this.showToast('Price modifier updated!');
        });
    },
    deletePriceModifier(priceModifierID) {
      const confirmDelete = () => {
        this.showToast('Deleting price modifier...');

        return DataSvc.deletePriceModifier(this.eventForm.id, priceModifierID)
          .then(() => {
            this.addEditPriceModifierModalOpen = false;
            this.showToast('Price modifier deleted!');
          });
      };

      return this.showConfirm(
        'Are you sure you want to delete this price modifier?',
        'Deleting a price modifier',
      ).then(confirmDelete);
    },
    getPriceModifiers(formID) {
      return DataSvc.getPriceModifiers(formID)
        .then((priceModifiers) => {
          this.formPriceModifiers = priceModifiers;
        });
    },
  },
  created() {
    this.showGlobalLoader('Loading event form...');

    const initEventForm = () => {
      const { eventID, formID } = this.$route.params;

      this.getFormAndOrgFields(formID);

      // get event
      const eventPromise = DataSvc.getEvent(eventID)
        .then((event) => {
          this.event = event;

          if (this.loggedInUser.actingOrg.id !== event.org_id) {
            this.findAndSwitchActingOrg(event.org_id);
          }
        })
        .finally(() => {
          this.$nextTick(() => {
            // watch for org change and route to events page
            EventBus.$on('acting-org-changed', this.goToEvents);
          });
        });

      // get custom field types
      const typesPromise = DataSvc.getEventFormFieldTypes()
        .then((fieldTypes) => {
          const typeMap = {
            'static-header': { text: 'Heading', type: 'Display Only' },
            'static-paragraph': { text: 'Text', type: 'Display Only' },
            'static-link': { text: 'Link', type: 'Display Only' },
            text: { text: 'Text Input', type: 'Input' },
            number: { text: 'Number Input', type: 'Input' },
            textarea: { text: 'Text Input (Long Form)', type: 'Input' },
            checkbox: { text: 'Checkbox Input', type: 'Input' },
            dropdown: { text: 'Dropdown Input', type: 'Input' },
          };

          this.fieldTypeOptions = _(fieldTypes)
            .reject(type => type.includes('layout'))
            .orderBy()
            .map(type => ({
              type: typeMap[type].type,
              text: typeMap[type].text,
              value: type,
            }))
            .sortBy('text')
            .groupBy('type')
            .value();
        });

      // get all forms for org
      const formsPromise = DataSvc.getForms({
        organization_id: this.loggedInUser.actingOrg.id,
      }).then((forms) => {
        this.forms = _(forms)
          .filter(f => f.form_fields.length > 0)
          .orderBy('name')
          .value();
      });

      const priceModifiersPromise = this.getPriceModifiers(formID);

      return Promise.all([
        eventPromise,
        typesPromise,
        formsPromise,
        priceModifiersPromise,
      ]);
    };

    this.initPromise.then(initEventForm).finally(this.hideGlobalLoader);
  },
  destroyed() {
    EventBus.$off('acting-org-changed', this.goToEvents);
  },
};
</script>

<style lang="scss">
#event-form {
  #form-preview {
    *[disabled] {
      background: #ffffff;
    }

    textarea {
      resize: none;
    }

    .form-field {
      hr {
        margin-top: 0;
      }
    }

    #empty-form {
      pointer-events: none;
    }
  }

  #layout-add {
    .dropdown-toggle {
      padding: 0px 0 0 1px;
    }

    svg {
      transform: rotate(90deg);
    }
  }
}
</style>
